// Formats français (dates, nombres) même quand un écran ne précise pas la langue
import './utils/locale'
import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js'; // toujours utile pour modals, toasts, collapses, etc.

import '@fortawesome/fontawesome-free/css/all.css';
import '@fortawesome/fontawesome-free/js/all.js';

import 'bootstrap-icons/font/bootstrap-icons.css';

import Multiselect from '@vueform/multiselect'
import '@vueform/multiselect/themes/default.css'

// Textes en français par défaut pour toutes les listes de recherche
// (chaque écran importe le même composant : ses valeurs par défaut s'appliquent partout)
const textesMultiselect = { noOptionsText: 'La liste est vide', noResultsText: 'Aucun résultat' }
for (const [prop, texte] of Object.entries(textesMultiselect)) {
  const p = Multiselect.props?.[prop]
  if (p && typeof p === 'object') p.default = texte
}


import { installerSurveillance, installerHorsLigne } from './surveillance/erreurs'

const app = createApp(App);

// Erreurs JavaScript des postes remontées à la supervision de la plateforme
installerSurveillance(app);

// L'application s'ouvre même sans internet (copie de l'interface sur le poste)
installerHorsLigne();

// Navigateur rouvert sans session : aucune copie de patients ne doit rester sur le poste
if (!localStorage.getItem('token')) {
  import('./horsLigne/reference').then(m => m.effacerDonneesLocales()).catch(() => {});
}

/**
 * DIRECTIVE : v-dropdown-fixed
 * -----------------------------------------------------------
 * Implémentation 100% maison, indépendante de la classe Dropdown
 * de Bootstrap et de son système data-api.
 *
 * Pourquoi : le data-api de Bootstrap ([data-bs-toggle="dropdown"])
 * enregistre un écouteur de clic global sur `document`. Si le module
 * Bootstrap est chargé/importé plus d'une fois dans le bundle (ce qui
 * arrivait avec l'import séparé `from 'bootstrap'`), on se retrouve
 * avec DEUX écouteurs concurrents qui s'annulent l'un l'autre : le
 * menu s'ouvre puis se referme aussitôt. Plutôt que de dépendre d'une
 * config d'import fragile, on gère tout nous-mêmes ici :
 *  - un seul écouteur de clic sur le bouton,
 *  - positionnement en `position: fixed` calculé à la main
 *    (jamais coupé par un `.table-responsive` en overflow),
 *  - fermeture au clic extérieur et au scroll.
 *
 * Usage : gardez `data-bs-toggle="dropdown"` pour le style visuel
 * (icône, etc. si besoin), mais AJOUTEZ `v-dropdown-fixed` sur le
 * <button>. Le comportement d'ouverture est entièrement pris en
 * charge par cette directive, plus par Bootstrap.
 */
app.directive('dropdown-fixed', {
  mounted(el) {

    const menu = el.parentElement?.querySelector('.dropdown-menu');
    if (!menu) return;

    // On empêche Bootstrap de gérer ce bouton, pour éviter tout conflit.
    el.removeAttribute('data-bs-toggle');

    const closeMenu = () => {
      menu.classList.remove('show');
      el.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onClickOutside, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', closeMenu);
    };

    // Défilement de la page : on ferme ; défilement DANS le menu (long menu) : on garde
    const onScroll = (e) => {
      if (!menu.contains(e.target)) closeMenu();
    };

    const onClickOutside = (e) => {
      if (!menu.contains(e.target) && e.target !== el) {
        closeMenu();
      }
    };

    const openMenu = () => {
      // Ferme tout autre dropdown déjà ouvert sur la page
      document.querySelectorAll('.dropdown-menu.show').forEach(m => {
        if (m !== menu) m.classList.remove('show');
      });

      const rect = el.getBoundingClientRect();

      menu.style.position = 'fixed';
      menu.style.margin = '0';
      menu.style.zIndex = '2000';
      menu.style.maxHeight = '';
      menu.style.overflowY = '';

      // Hauteur réelle du menu, puis place disponible sous et au-dessus du bouton :
      // le menu s'ouvre vers le haut s'il ne tient pas en bas, et défile s'il est
      // plus haut que l'écran (sinon les dernières actions sortaient de l'écran)
      menu.style.visibility = 'hidden';
      menu.classList.add('show');
      const hauteur = menu.scrollHeight;
      menu.classList.remove('show');
      menu.style.visibility = '';
      const marge = 8;
      const dessous = window.innerHeight - rect.bottom - marge;
      const dessus = rect.top - marge;
      if (hauteur > dessous && dessus > dessous) {
        const h = Math.min(hauteur, dessus);
        menu.style.top = `${Math.max(marge, rect.top - h - 2)}px`;
        if (hauteur > dessus) menu.style.maxHeight = `${h}px`;
      } else {
        menu.style.top = `${rect.bottom + 2}px`;
        if (hauteur > dessous) menu.style.maxHeight = `${Math.max(dessous, 120)}px`;
      }
      menu.style.overflowY = 'auto';

      // Aligne à droite si le menu déborderait de l'écran
      const menuWidth = menu.offsetWidth || 180;
      const overflowsRight = rect.left + menuWidth > window.innerWidth;

      if (overflowsRight) {
        menu.style.left = 'auto';
        menu.style.right = `${window.innerWidth - rect.right}px`;
      } else {
        menu.style.left = `${rect.left}px`;
        menu.style.right = 'auto';
      }

      menu.classList.add('show');
      el.setAttribute('aria-expanded', 'true');

      document.addEventListener('click', onClickOutside, true);
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', closeMenu);
    };

    const onToggleClick = (e) => {
      e.stopPropagation();
      if (menu.classList.contains('show')) {
        closeMenu();
      } else {
        openMenu();
      }
    };

    el.addEventListener('click', onToggleClick);

    // Nettoyage stocké sur l'élément pour beforeUnmount
    el._dropdownFixedCleanup = () => {
      el.removeEventListener('click', onToggleClick);
      document.removeEventListener('click', onClickOutside, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', closeMenu);
    };
  },

  beforeUnmount(el) {
    if (el._dropdownFixedCleanup) {
      el._dropdownFixedCleanup();
    }
  }
});

app
  .use(store)
  .use(router)
  .component('Multiselect', Multiselect)
  .mount('#app')